import { Activity, Layers, PieChart, Repeat, Shuffle, TrendingDown, Workflow } from 'lucide-react';
import { formatCount } from '@/shared/analytics/MetricFormatting';
import { formatSignalValue } from '@/shared/signals/SignalFormatting';
import { aggregateSeriesLoader, trendSeriesLoader } from '@/shared/signals/SignalLiveLoaders';
import { alertsSample, kpiSample, seriesSample, trendSample } from '@/shared/signals/SignalSampleData';
import type { SignalData, SignalTileDef } from '@/shared/signals/SignalTypes';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const SESSION_DRIFT_TEMPLATES = [
    { label: 'Thread spans code review → travel planning', detail: 'Engineering · 11 prompts', severity: 'high' as const },
    { label: 'Drafting drifts into unrelated research', detail: 'Marketing · 8 prompts', severity: 'medium' as const },
    { label: 'Single session covers 4 task categories', detail: 'Operations', severity: 'low' as const },
];

const FRUSTRATION_TEMPLATES = [
    { label: 'Abrupt end, then near-identical re-prompt', detail: '5 restarts in 10 min · Support', severity: 'high' as const },
    { label: 'Repeated rephrasing of the same ask', detail: 'Finance', severity: 'medium' as const },
    { label: 'Session abandoned mid-task', detail: 'Sales', severity: 'low' as const },
];

const DECLINING_TEMPLATES = [
    { label: 'Pilot cohort trending toward shelf-ware', detail: 'Legal · -64% prompts vs. launch', severity: 'high' as const },
    { label: 'Power user activity decaying', detail: '3 users · Product', severity: 'medium' as const },
    { label: 'Team usage flat for 3 weeks', detail: 'HR', severity: 'low' as const },
];

/**
 * The Behavior landing dashboard. Use-case mix, difficulty tiers, conversation depth,
 * activity spikes, and model split are live from the analytics OLAP gateway
 * (interactions group-bys, totals, and day-bucketed trends). Use-case expansion is
 * live too — the average distinct task categories per active user, counted from a
 * (user × category) group-by. Session drift (sessions spanning multiple classified
 * topics) and declining-activity alerts (users whose recent half-window collapsed
 * below their prior baseline) are live from the gateway's anomaly queries.
 * Frustration signals are live too — a per-user re-prompt detector over the
 * `interaction_text` (silver) source, which carries the raw prompt content the
 * gold layer omits: near-identical normalized prompts re-submitted by the same
 * user within minutes. It stays on sample for providers that don't capture
 * prompt text (no content to hash → no detections).
 */
export const BEHAVIOR_SIGNALS: SignalTileDef[] = [
    {
        id: 'primary-use-cases',
        title: 'Primary Use Cases',
        description: 'The dominant work types people drive the AI toward — the task-category mix that defines how the org actually uses its seats.',
        icon: PieChart,
        accent: 'indigo',
        kind: 'donut',
        to: '/behavior/use-cases',
        sample: (rng) => seriesSample(rng, ['Code Generation', 'Data Analysis', 'Summarization', 'Drafting', 'Research', 'Translation'], { min: 40, max: 520, format: 'count' }),
        live: aggregateSeriesLoader({ groupBy: 'task_category', metric: 'turns' }),
    },
    {
        id: 'session-drift',
        title: 'Session Drift',
        description: 'Threads that wander across unrelated topics within a single session, burning redundant context tokens on each pivot.',
        icon: Shuffle,
        accent: 'teal',
        kind: 'alerts',
        span: 2,
        to: '/behavior/session-drift',
        sample: (rng) => alertsSample(rng, SESSION_DRIFT_TEMPLATES, [3, 14]),
        live: async ({ gateway, filters }): Promise<SignalData> => {
            const res = await gateway.anomalies({ kind: 'session_drift', filters });
            if (res.total === 0) throw new Error('no session drift detected');
            return { alerts: res.events, alertCount: res.total };
        },
    },
    {
        id: 'skill-complexity',
        title: 'Skill & Complexity',
        description: 'How interactions distribute across difficulty tiers — a read on whether the org leans on the AI for routine or genuinely hard work.',
        icon: Layers,
        accent: 'amber',
        kind: 'bars',
        to: '/behavior/difficulty-mix',
        sample: (rng) => seriesSample(rng, ['Low', 'Medium', 'High'], { min: 60, max: 640, format: 'count', sort: false }),
        live: aggregateSeriesLoader({ groupBy: 'difficulty', metric: 'turns' }),
    },
    {
        id: 'use-case-expansion',
        title: 'Use Case Expansion',
        description: 'How broadly a cohort spreads across intent classes — distinguishing single-purpose adopters from multi-workflow power users.',
        icon: Workflow,
        accent: 'violet',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 2, max: 11, format: 'count', caption: 'distinct intent classes / active user', deltaPct: [-4, 22], goodWhen: 'up', spark: 'up' }),
        live: async ({ gateway, filters }): Promise<SignalData> => {
            const res = await gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'task_category'], metrics: ['turns'], filters });
            const classesByUser = new Map<string, Set<string>>();
            for (const row of res.rows) {
                const user = row.keys.user_id;
                const category = row.keys.task_category;
                if (user === undefined || user === '—' || category === undefined || category === '—') continue;
                const set = classesByUser.get(user) ?? new Set<string>();
                set.add(category);
                classesByUser.set(user, set);
            }
            if (classesByUser.size === 0) throw new Error('no per-user intent classes to chart');
            const avg = [...classesByUser.values()].reduce((sum, set) => sum + set.size, 0) / classesByUser.size;
            return { headline: avg.toFixed(1), format: 'count', caption: 'distinct intent classes / active user' };
        },
    },
    {
        id: 'conversation-depth',
        title: 'Conversation Depth',
        description: 'The average number of back-and-forth prompts per session — a proxy for how iteratively people work with the model.',
        icon: Repeat,
        accent: 'rose',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 2, max: 9, format: 'ratio', caption: 'avg prompts per session', spark: 'flat' }),
        live: async (ctx): Promise<SignalData> => {
            const res = await ctx.gateway.aggregate({ source: 'interactions', groupBy: [], metrics: ['turns', 'sessions'], filters: ctx.filters });
            const depth = res.totals.sessions > 0 ? res.totals.turns / res.totals.sessions : 0;
            return { headline: formatSignalValue('ratio', depth), format: 'ratio', caption: `avg prompts across ${formatCount(res.totals.sessions)} sessions` };
        },
    },
    {
        id: 'frustration-signals',
        title: 'Frustration Signals',
        description: 'Abrupt session endings followed by near-identical prompt restarts — a behavioral proxy for failed or unsatisfying responses.',
        icon: TrendingDown,
        accent: 'cyan',
        kind: 'alerts',
        span: 2,
        to: '/behavior/frustration-signals',
        sample: (rng) => alertsSample(rng, FRUSTRATION_TEMPLATES, [2, 12]),
        live: async ({ gateway, filters }): Promise<SignalData> => {
            const res = await gateway.anomalies({ kind: 're_prompt', filters });
            if (res.total === 0) throw new Error('no re-prompt frustration detected');
            return { alerts: res.events, alertCount: res.total };
        },
    },
    {
        id: 'activity-spikes',
        title: 'Activity Spikes',
        description: 'Anomalous day-over-day surges in token and session activity — the signal behind sudden adoption or a runaway workflow.',
        icon: Activity,
        accent: 'indigo',
        kind: 'trend',
        to: '/behavior/activity-trend',
        sample: (rng) => trendSample(rng, DAY_LABELS, 180, 1400, 'up'),
        live: trendSeriesLoader({ bucket: 'day', metric: 'turns', format: 'compact' }),
    },
    {
        id: 'declining-activity-alerts',
        title: 'Declining Activity Alerts',
        description: 'Cohorts whose usage is decaying toward churned or shelf-ware status — the early warning for at-risk adoption.',
        icon: TrendingDown,
        accent: 'violet',
        kind: 'alerts',
        span: 2,
        sample: (rng) => alertsSample(rng, DECLINING_TEMPLATES, [2, 9]),
        live: async ({ gateway, filters }): Promise<SignalData> => {
            const res = await gateway.anomalies({ kind: 'declining_activity', filters });
            if (res.total === 0) throw new Error('no declining cohorts detected');
            return { alerts: res.events, alertCount: res.total };
        },
    },
];
